import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
  PieChart, Pie, AreaChart, Area
} from 'recharts';
import {
  DollarSign, TrendingUp, Clock, AlertTriangle, Trophy, FolderOpen, Users, Loader2,
  ArrowUpRight, ArrowDownRight, CalendarRange, FileText, Target, Package, Layers, CheckCircle2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: '#94a3b8', active: '#3b82f6', analyzing: '#1D4053', completed: '#10b981',
  quoted: '#8b5cf6', won: '#059669', lost: '#ef4444', expired: '#f97316', error: '#dc2626',
  draft: '#94a3b8', sent: '#3b82f6', viewed: '#8b5cf6', accepted: '#10b981', rejected: '#ef4444', revised: '#f59e0b'
};

const PIPELINE_COLORS = { won: '#059669', pending: '#3b82f6', expired: '#f97316' };
const TYPE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4', '#ec4899', '#14b8a6'];

const fmtCurrency = (v) => {
  if (!v && v !== 0) return '$0';
  if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-white border border-[#E2E8ED] rounded-xl p-3 shadow-lg text-xs">
      <p className="font-semibold text-[#0D1830] mb-2">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4 py-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
            <span className="capitalize text-[#446E87]">{p.dataKey}</span>
          </span>
          <span className="font-semibold text-[#0D1830]">{fmtCurrency(p.value)}</span>
        </div>
      ))}
      <div className="border-t border-[#E2E8ED] mt-1.5 pt-1.5 flex justify-between">
        <span className="text-[#72A4BF]">Total</span>
        <span className="font-bold text-[#0D1830]">{fmtCurrency(payload.reduce((s, p) => s + (p.value || 0), 0))}</span>
      </div>
    </div>
  );
};

function ComparisonBadge({ value }) {
  const isPositive = value > 0;
  const isZero = value === 0;
  return (
    <span className={`flex items-center gap-0.5 text-[10px] font-medium ${isZero ? 'text-[#72A4BF]' : isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
      {!isZero && (isPositive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />)}
      {isPositive ? '+' : ''}{value}% vs last period
    </span>
  );
}

export default function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('6');

  useEffect(() => { fetchAll(); }, [timeRange]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, pipeRes, reportRes] = await Promise.all([
        fetch(`${API}/dashboard/stats`, { credentials: 'include' }),
        fetch(`${API}/dashboard/pipeline?months=${timeRange}`, { credentials: 'include' }),
        fetch(`${API}/reports/dashboard`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (pipeRes.ok) setPipeline(await pipeRes.json());
      if (reportRes.ok) setReportData(await reportRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const totals = pipeline?.totals || { won: 0, pending: 0, expired: 0, total: 0 };
  const comp = stats?.comparison || {};

  const pipelineKpis = [
    { label: 'Total Pipeline', value: totals.total, icon: DollarSign, color: 'text-[#0D1830]', bg: 'bg-[#0D1830]/5' },
    { label: 'Won', value: totals.won, icon: Trophy, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Pending', value: totals.pending, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Expired', value: totals.expired, icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
  ];

  const totalMargin = reportData?.total_quote_value && reportData?.total_quote_cost
    ? (((reportData.total_quote_value - reportData.total_quote_cost) / reportData.total_quote_value) * 100).toFixed(1)
    : 0;

  return (
    <div className="min-h-full bg-[#F7FAFB]" data-testid="reports-page">
      <main className="max-w-[1440px] mx-auto px-6 lg:px-10 py-8">
        <h1 className="text-2xl font-bold text-[#0D1830] mb-1">Reports & Analytics</h1>
        <p className="text-sm text-[#72A4BF] mb-8">Business intelligence, pipeline forecast, and performance metrics</p>

        {loading ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" />
          </div>
        ) : (
          <>
            {/* Portfolio Margin Hero */}
            {reportData && (
              <Card className="mb-8 border-[#1D4053]/20 shadow-none bg-gradient-to-r from-[#72A4BF]/10 to-white" data-testid="portfolio-margin">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#0D1830] font-medium">Overall Portfolio Margin</p>
                    <p className="text-4xl font-bold text-[#0D1830] mt-1">{totalMargin}%</p>
                    <p className="text-xs text-[#72A4BF] mt-1">Based on all quotes</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-[#446E87]">Revenue: <strong>${(reportData.total_quote_value || 0).toLocaleString()}</strong></p>
                    <p className="text-xs text-[#446E87]">Cost: <strong>${(reportData.total_quote_cost || 0).toLocaleString()}</strong></p>
                    <p className="text-xs text-[#0D1830] font-semibold mt-1">Profit: ${((reportData.total_quote_value || 0) - (reportData.total_quote_cost || 0)).toLocaleString()}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hero KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="hero-kpis">
              {[
                { label: 'Active Projects', value: stats?.active_projects || 0, icon: FolderOpen, color: 'text-blue-600', bg: 'bg-blue-50', comp: comp.active_projects, fmt: false },
                { label: 'Open Quotes', value: stats?.open_quotes || 0, icon: FileText, color: 'text-amber-600', bg: 'bg-amber-50', comp: comp.open_quotes, fmt: false },
                { label: 'Quote Value', value: stats?.total_quote_value || 0, icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50', comp: comp.quote_value, fmt: true },
                { label: 'Win Rate', value: `${stats?.win_rate || 0}%`, icon: Target, color: 'text-purple-600', bg: 'bg-purple-50', comp: comp.win_rate, fmt: false },
                { label: 'Avg Quote Value', value: stats?.avg_quote_value || 0, icon: TrendingUp, color: 'text-[#0D1830]', bg: 'bg-[#0D1830]/5', comp: comp.avg_quote_value, fmt: true },
              ].map((kpi, i) => (
                <Card key={i} className="border-[#E2E8ED] shadow-none" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-[#72A4BF] font-medium">{kpi.label}</p>
                      <div className={`w-8 h-8 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                        <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
                      </div>
                    </div>
                    <p className={`text-2xl font-bold ${kpi.color}`}>
                      {kpi.fmt ? fmtCurrency(kpi.value) : kpi.value}
                    </p>
                    <ComparisonBadge value={kpi.comp || 0} />
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quote Value Trend + Win Rate by Type */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              <Card className="border-[#E2E8ED] shadow-none" data-testid="quote-value-trend">
                <CardHeader className="pb-1 pt-4 px-5">
                  <CardTitle className="text-sm font-bold text-[#0D1830]">Quote Value Trend</CardTitle>
                  <p className="text-xs text-[#72A4BF]">Last 6 months quote values</p>
                </CardHeader>
                <CardContent className="px-5 pb-4">
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={stats?.quote_value_trend || []} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <defs>
                        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.15} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8ED" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#72A4BF' }} axisLine={false} tickLine={false} />
                      <YAxis tickFormatter={fmtCurrency} tick={{ fontSize: 11, fill: '#72A4BF' }} axisLine={false} tickLine={false} width={55} />
                      <Tooltip formatter={(v) => [fmtCurrency(v), 'Quote Value']} />
                      <Area type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} fill="url(#trendFill)" dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#fff' }} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-[#E2E8ED] shadow-none" data-testid="win-rate-by-type">
                <CardHeader className="pb-1 pt-4 px-5">
                  <CardTitle className="text-sm font-bold text-[#0D1830]">Win Rate by Project Type</CardTitle>
                  <p className="text-xs text-[#72A4BF]">Percentage of won quotes by category</p>
                </CardHeader>
                <CardContent className="px-5 pb-4">
                  {(stats?.win_rate_by_type || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie data={stats.win_rate_by_type} cx="50%" cy="45%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value" nameKey="name">
                          {stats.win_rate_by_type.map((_, i) => (<Cell key={i} fill={TYPE_COLORS[i % TYPE_COLORS.length]} />))}
                        </Pie>
                        <Tooltip formatter={(v, name) => [`${v}${typeof v === 'number' && v <= 100 ? '%' : ''}`, name]} />
                        <Legend verticalAlign="bottom" iconType="circle" iconSize={8}
                          formatter={(val) => <span className="text-xs capitalize text-[#446E87]">{val}</span>}
                          wrapperStyle={{ paddingTop: '8px', fontSize: '12px' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-[260px] text-sm text-[#72A4BF]">No Data</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pipeline Forecast */}
            <div className="mb-8" data-testid="pipeline-section">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-xl font-bold text-[#0D1830]">Pipeline Forecast</h2>
                  <p className="text-sm text-[#446E87] mt-0.5">Quote values by bid end date</p>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarRange className="w-4 h-4 text-[#72A4BF]" />
                  <Select value={timeRange} onValueChange={setTimeRange}>
                    <SelectTrigger className="w-36 h-9 rounded-full text-sm" data-testid="pipeline-range">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 Months</SelectItem>
                      <SelectItem value="6">6 Months</SelectItem>
                      <SelectItem value="12">12 Months</SelectItem>
                      <SelectItem value="24">24 Months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                {pipelineKpis.map((kpi, i) => (
                  <Card key={i} className="border-[#E2E8ED] shadow-none" data-testid={`pipeline-kpi-${kpi.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-[#72A4BF] font-medium uppercase tracking-wide">{kpi.label}</p>
                          <p className={`text-xl font-bold mt-1 ${kpi.color}`}>{fmtCurrency(kpi.value)}</p>
                        </div>
                        <div className={`w-10 h-10 rounded-xl ${kpi.bg} flex items-center justify-center`}>
                          <kpi.icon className={`w-5 h-5 ${kpi.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-[#E2E8ED] shadow-none" data-testid="pipeline-chart">
                <CardContent className="p-5">
                  <ResponsiveContainer width="100%" height={340}>
                    <BarChart data={pipeline?.chart_data || []} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8ED" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#72A4BF' }} axisLine={{ stroke: '#E2E8ED' }} tickLine={false} />
                      <YAxis tickFormatter={fmtCurrency} tick={{ fontSize: 11, fill: '#72A4BF' }} axisLine={false} tickLine={false} width={65} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ paddingTop: '12px', fontSize: '12px' }} formatter={(val) => <span className="capitalize text-[#446E87] text-xs">{val}</span>} />
                      <Bar dataKey="won" stackId="pipeline" fill={PIPELINE_COLORS.won} radius={[0, 0, 0, 0]} name="Won" />
                      <Bar dataKey="pending" stackId="pipeline" fill={PIPELINE_COLORS.pending} radius={[0, 0, 0, 0]} name="Pending" />
                      <Bar dataKey="expired" stackId="pipeline" fill={PIPELINE_COLORS.expired} radius={[4, 4, 0, 0]} name="Expired" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="border-[#E2E8ED] shadow-none mt-4" data-testid="pipeline-table">
                <CardHeader className="pb-2 pt-4 px-5">
                  <CardTitle className="text-sm font-semibold text-[#0D1830]">Pipeline Projects</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {(pipeline?.projects || []).length === 0 ? (
                    <div className="text-center py-10 text-sm text-[#72A4BF]">
                      <FolderOpen className="w-6 h-6 mx-auto mb-2 text-[#72A4BF]/40" />
                      No projects with bid dates and quotes in this time range
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#E2E8ED] hover:bg-transparent">
                          <TableHead className="text-[10px] uppercase tracking-wide text-[#72A4BF] font-semibold">Project</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide text-[#72A4BF] font-semibold">Client</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide text-[#72A4BF] font-semibold">End Date</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide text-[#72A4BF] font-semibold">Status</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide text-[#72A4BF] font-semibold text-right">Value</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wide text-[#72A4BF] font-semibold text-right">Quotes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(pipeline?.projects || []).map((p) => (
                          <TableRow key={p.project_id} className="border-[#E2E8ED] hover:bg-[#72A4BF]/5 cursor-pointer"
                            onClick={() => window.location.href = `/projects/${p.project_id}`}>
                            <TableCell className="text-sm font-medium text-[#0D1830]">{p.name}</TableCell>
                            <TableCell className="text-sm text-[#446E87]">{p.client_name || '—'}</TableCell>
                            <TableCell className="text-sm text-[#446E87]">
                              {p.bid_end_date ? new Date(p.bid_end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-[10px] rounded-full px-2 py-0.5 border-0 font-medium capitalize ${
                                p.category === 'won' ? 'bg-emerald-100 text-emerald-700' :
                                p.category === 'expired' ? 'bg-orange-100 text-orange-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>{p.category}</Badge>
                            </TableCell>
                            <TableCell className="text-sm font-semibold text-right text-[#0D1830]">{fmtCurrency(p.total_value)}</TableCell>
                            <TableCell className="text-sm text-right text-[#446E87]">{p.quote_count}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Overview Stats */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-[#0D1830] mb-4">Overview</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {[
                  { label: 'Total Projects', value: stats?.total_projects || 0, icon: FolderOpen },
                  { label: 'Active Projects', value: stats?.active_projects || 0, icon: TrendingUp },
                  { label: 'Total Quotes', value: stats?.total_quotes || 0, icon: DollarSign },
                  { label: 'Win Rate', value: `${stats?.win_rate || 0}%`, icon: Trophy },
                  { label: 'Team', value: stats?.team_count || 0, icon: Users },
                ].map((s, i) => (
                  <Card key={i} className="border-[#E2E8ED] shadow-none" data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-[#72A4BF]/10 flex items-center justify-center shrink-0">
                          <s.icon className="w-4 h-4 text-[#1D4053]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#72A4BF]">{s.label}</p>
                          <p className="text-lg font-bold text-[#0D1830]">{s.value}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Projects by Status + Quote Pipeline + Top Divisions */}
            {stats && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                {stats.projects_by_status && Object.keys(stats.projects_by_status).length > 0 && (
                  <Card className="border-[#E2E8ED] shadow-none" data-testid="projects-status-chart">
                    <CardHeader className="pb-2 pt-4 px-5">
                      <CardTitle className="text-sm font-semibold text-[#0D1830]">Projects by Status</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4">
                      <ResponsiveContainer width="100%" height={220}>
                        <PieChart>
                          <Pie
                            data={Object.entries(stats.projects_by_status).map(([name, value]) => ({ name, value }))}
                            cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value"
                          >
                            {Object.entries(stats.projects_by_status).map(([status], i) => (
                              <Cell key={i} fill={STATUS_COLORS[status] || '#94a3b8'} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v, name) => [v, name]} />
                          <Legend verticalAlign="bottom" iconType="circle" iconSize={8}
                            formatter={(val) => <span className="text-xs capitalize text-[#446E87]">{val}</span>}
                            wrapperStyle={{ paddingTop: '12px', fontSize: '12px' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {stats.quotes_by_status && Object.keys(stats.quotes_by_status).length > 0 && (
                  <Card className="border-[#E2E8ED] shadow-none" data-testid="quotes-status-chart">
                    <CardHeader className="pb-2 pt-4 px-5">
                      <CardTitle className="text-sm font-semibold text-[#0D1830]">Quote Pipeline</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={Object.entries(stats.quotes_by_status).map(([name, value]) => ({ name, value }))} layout="vertical" margin={{ left: 10 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8ED" />
                          <XAxis type="number" tick={{ fontSize: 11, fill: '#72A4BF' }} axisLine={false} tickLine={false} />
                          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#446E87', textTransform: 'capitalize' }} axisLine={false} tickLine={false} width={70} />
                          <Tooltip formatter={(v) => [v, 'Quotes']} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {Object.entries(stats.quotes_by_status).map(([status], i) => (
                              <Cell key={i} fill={STATUS_COLORS[status] || '#94a3b8'} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {stats.top_divisions?.length > 0 && (
                  <Card className="border-[#E2E8ED] shadow-none" data-testid="top-divisions-chart">
                    <CardHeader className="pb-2 pt-4 px-5">
                      <CardTitle className="text-sm font-semibold text-[#0D1830]">Top Divisions</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4">
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={stats.top_divisions} margin={{ left: -10 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8ED" />
                          <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#72A4BF' }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={50} />
                          <YAxis tickFormatter={fmtCurrency} tick={{ fontSize: 11, fill: '#72A4BF' }} axisLine={false} tickLine={false} />
                          <Tooltip formatter={(v) => [fmtCurrency(v), 'Value']} />
                          <Bar dataKey="value" fill="#1D4053" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* Recent Activity */}
            {stats && (stats.recent_projects?.length > 0 || stats.recent_quotes?.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {stats.recent_projects?.length > 0 && (
                  <Card className="border-[#E2E8ED] shadow-none" data-testid="recent-projects">
                    <CardHeader className="pb-2 pt-4 px-5">
                      <CardTitle className="text-sm font-semibold text-[#0D1830]">Recent Projects</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4 space-y-2">
                      {stats.recent_projects.map((p, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#E2E8ED] last:border-0">
                          <div>
                            <p className="text-sm font-medium text-[#0D1830]">{p.name}</p>
                            <p className="text-[10px] text-[#72A4BF]">{new Date(p.created_at).toLocaleDateString()}</p>
                          </div>
                          <Badge className="text-[10px] capitalize rounded-full px-2 py-0.5 border-0"
                            style={{ backgroundColor: (STATUS_COLORS[p.status] || '#ccc') + '20', color: STATUS_COLORS[p.status] || '#999' }}>
                            {p.status}
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                {stats.recent_quotes?.length > 0 && (
                  <Card className="border-[#E2E8ED] shadow-none" data-testid="recent-quotes">
                    <CardHeader className="pb-2 pt-4 px-5">
                      <CardTitle className="text-sm font-semibold text-[#0D1830]">Recent Quotes</CardTitle>
                    </CardHeader>
                    <CardContent className="px-5 pb-4 space-y-2">
                      {stats.recent_quotes.map((q, i) => (
                        <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#E2E8ED] last:border-0">
                          <div>
                            <p className="text-sm font-medium text-[#0D1830]">{q.quote_name || q.customer_name}</p>
                            <p className="text-[10px] text-[#72A4BF]">{fmtCurrency(q.total_price)}</p>
                          </div>
                          <Badge className="text-[10px] capitalize rounded-full px-2 py-0.5 border-0"
                            style={{ backgroundColor: (STATUS_COLORS[q.status] || '#ccc') + '20', color: STATUS_COLORS[q.status] || '#999' }}>
                            {q.status}
                          </Badge>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
